import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { logInternalError } from '../../lib/errors';
import { Button as MantineButton, CopyButton, Text } from '@mantine/core';
import { ReactComponent as ContentCopyIcon } from '../../assets/images/icons/icon_content_copy.svg';
import { ReactComponent as ShareIcon } from '../../assets/images/icons/icon_share.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { useMediaQuery } from '@mantine/hooks';
import * as events from '../../lib/analytics/events';
import { getDomainConfig } from '../../lib/domainConfig';

const getShareInformation = (code: string, source: string, t: TFunction) => ({
  title: t('check_out_the_fi_smart_dog_collar'),
  text: t('use_my_referral_code_when_you_buy', { code }),
  url: `https://${getDomainConfig().shopDomain}/r/${code}/?utm_source=${source}`,
});

function SimpleShare({ code, source }: ReferralCodeProps) {
  const { t } = useTranslation();
  const shareData = getShareInformation(code, source, t);
  const largeScreen = useMediaQuery('(min-width: 1024px)');
  const isTouch = useMediaQuery('(hover: none)');
  const canShare = !!navigator.share && navigator.canShare(shareData);

  const rightIconStyle = { height: 18, width: 18, fill: 'white' };

  const handleClick = (copy: () => void) => {
    if (!largeScreen && canShare && isTouch) {
      events.referrals.nativeShareClicked();
      navigator
        .share(shareData)
        .then(() => {
          events.referrals.nativeShareSuccess();
        })
        .catch((err: Error) => {
          if (err.toString().includes('AbortError')) {
            events.referrals.nativeShareCanceled();
            return;
          }
          logInternalError(err);
        });
    } else {
      copy();
      events.referrals.codeCopied();
    }
  };

  return (
    <CopyButton value={shareData.url}>
      {({ copied, copy }) => {
        return (
          <MantineButton
            sx={{ backgroundColor: 'black', '&:hover': { backgroundColor: '#333' } }}
            radius={100}
            onClick={() => handleClick(copy)}
            p={'10px 24px'}
            h={38}
            rightIcon={
              copied ? (
                <CheckIcon style={rightIconStyle} />
              ) : largeScreen ? (
                <ContentCopyIcon style={rightIconStyle} />
              ) : (
                <ShareIcon style={rightIconStyle} />
              )
            }
          >
            <Text color="white" fz={14} fw={600}>
              {copied ? t('copied') : t('share_fi')}
            </Text>
          </MantineButton>
        );
      }}
    </CopyButton>
  );
}

interface ReferralCodeProps {
  code: string;
  source: string;
}

export default function ReferralCode({ code, source }: ReferralCodeProps) {
  return <SimpleShare code={code} source={source} />;
}
